import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { waybillsApi } from '../../api/waybill';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { RecordHeader } from '../../components/RecordHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { useToast } from '../../components/Toast';
import { WAYBILL_STATUS_LABELS } from './WaybillsListPage';
import { WAYBILL_STAGES } from '../../config/workflow-stages';
import { formatDate } from '../../utils/formatDate';

const CONDITION_LABELS: Record<string, string> = { good: 'Good - No Issues', partial_damage: 'Partial Damage/Shortage', significant_damage: 'Significant Damage/Shortage' };
const DISPATCH_STATUSES = ['preparing', 'ready', 'dispatched', 'in_transit'];

export function WaybillWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const waybillId = Number(id);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: wbData, isError } = useQuery({ queryKey: ['waybill', waybillId], queryFn: () => waybillsApi.get(waybillId) });
  const waybill = wbData?.data;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['waybill', waybillId] });

  const { data: itemsData } = useQuery({ queryKey: ['waybill-items', waybillId], queryFn: () => waybillsApi.listItems(waybillId) });
  const items = itemsData?.data ?? [];
  const { data: docsData } = useQuery({ queryKey: ['waybill-documents', waybillId], queryFn: () => genericDocumentApi.listFor('waybill', waybillId) });

  const [itemForm, setItemForm] = useState({ description: '', quantity: '', unit: '' });
  const addItemMutation = useMutation({
    mutationFn: () => waybillsApi.addItem(waybillId, { description: itemForm.description, quantity: Number(itemForm.quantity), unit: itemForm.unit || undefined }),
    onSuccess: () => { showSuccess('Item added.'); setItemForm({ description: '', quantity: '', unit: '' }); queryClient.invalidateQueries({ queryKey: ['waybill-items', waybillId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: (status: string) => waybillsApi.setStatus(waybillId, status),
    onSuccess: () => { showSuccess('Status updated.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [podForm, setPodForm] = useState({ receivedByName: '', receivedDate: '', condition: 'good' });
  const recordReceiptMutation = useMutation({
    mutationFn: () => waybillsApi.recordReceipt(waybillId, podForm),
    onSuccess: () => { showSuccess('Delivery confirmed.'); invalidate(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const [itemReceiptForm, setItemReceiptForm] = useState<Record<number, { received: string; shortage: string; damage: string }>>({});
  const recordItemReceiptMutation = useMutation({
    mutationFn: (itemId: number) => waybillsApi.recordItemReceipt(itemId, {
      quantityReceived: Number(itemReceiptForm[itemId]?.received ?? 0),
      shortageQuantity: Number(itemReceiptForm[itemId]?.shortage ?? 0),
      damageQuantity: Number(itemReceiptForm[itemId]?.damage ?? 0),
    }),
    onSuccess: () => { showSuccess('Item receipt recorded.'); queryClient.invalidateQueries({ queryKey: ['waybill-items', waybillId] }); },
    onError: (err: ApiError) => showError(err.message),
  });

  if (isError) return <div className="page"><div className="error-state">Failed to load this waybill. Please try refreshing the page.</div></div>;
  if (!waybill) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  const isDelivered = ['delivered', 'partially_delivered'].includes(waybill.status);

  return (
    <div className="page">
      <RecordHeader
        title={waybill.waybill_reference}
        readOnly={false}
        entityType="waybill"
        entityId={waybillId}
        fields={[
          { label: 'Route', value: `${waybill.origin} → ${waybill.destination}` },
          { label: 'Recipient', value: waybill.recipient_name },
          { label: 'Dispatch Date', value: formatDate(waybill.dispatch_date) },
          { label: 'Status', value: <StatusBadge status={waybill.status} label={WAYBILL_STATUS_LABELS[waybill.status]} /> },
        ]}
      />

      <WorkspaceTabs
        tabs={[
          {
            key: 'items', label: 'Items', content: (
              <div>
                <SectionHeader title="Items Being Dispatched" />
                <table className="data-table">
                  <thead><tr><th>Description</th><th>Quantity</th><th>Received</th><th>Shortage</th><th>Damage</th>{!isDelivered && <th></th>}</tr></thead>
                  <tbody>
                    {items.map((i) => (
                      <tr key={i.id}>
                        <td>{i.description}</td>
                        <td>{i.quantity} {i.unit ?? ''}</td>
                        <td>{i.quantity_received ?? (isDelivered ? (
                          <input type="number" step="0.01" style={{ width: 70 }} value={itemReceiptForm[i.id]?.received ?? ''} onChange={(e) => setItemReceiptForm((s) => ({ ...s, [i.id]: { ...s[i.id], received: e.target.value } }))} />
                        ) : '-')}</td>
                        <td>{i.quantity_received !== null ? i.shortage_quantity : (isDelivered ? (
                          <input type="number" step="0.01" style={{ width: 60 }} value={itemReceiptForm[i.id]?.shortage ?? ''} onChange={(e) => setItemReceiptForm((s) => ({ ...s, [i.id]: { ...s[i.id], shortage: e.target.value } }))} />
                        ) : '-')}</td>
                        <td>{i.quantity_received !== null ? i.damage_quantity : (isDelivered ? (
                          <input type="number" step="0.01" style={{ width: 60 }} value={itemReceiptForm[i.id]?.damage ?? ''} onChange={(e) => setItemReceiptForm((s) => ({ ...s, [i.id]: { ...s[i.id], damage: e.target.value } }))} />
                        ) : '-')}</td>
                        {!isDelivered && <td></td>}
                        {isDelivered && i.quantity_received === null && (
                          <td><button onClick={() => recordItemReceiptMutation.mutate(i.id)} disabled={recordItemReceiptMutation.isPending || !itemReceiptForm[i.id]?.received}>Save</button></td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {DISPATCH_STATUSES.includes(waybill.status) && (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); addItemMutation.mutate(); }}>
                    <input placeholder="Description" value={itemForm.description} onChange={(e) => setItemForm((s) => ({ ...s, description: e.target.value }))} required />
                    <input type="number" step="0.01" placeholder="Quantity" value={itemForm.quantity} onChange={(e) => setItemForm((s) => ({ ...s, quantity: e.target.value }))} required />
                    <input placeholder="Unit" value={itemForm.unit} onChange={(e) => setItemForm((s) => ({ ...s, unit: e.target.value }))} />
                    <button type="submit" disabled={addItemMutation.isPending}>Add Item</button>
                  </form>
                )}
              </div>
            ),
          },
          {
            key: 'dispatch', label: 'Dispatch Status', content: (
              <div>
                <SectionHeader title="Dispatch Status" />
                {DISPATCH_STATUSES.includes(waybill.status) && (
                  <div className="form-actions">
                    {waybill.status === 'preparing' && <button onClick={() => statusMutation.mutate('ready')}>Mark Ready</button>}
                    {waybill.status === 'ready' && <button onClick={() => statusMutation.mutate('dispatched')}>Dispatch</button>}
                    {waybill.status === 'dispatched' && <button onClick={() => statusMutation.mutate('in_transit')}>Mark In Transit</button>}
                  </div>
                )}
                {!isDelivered && waybill.status !== 'cancelled' && waybill.status !== 'returned' && (
                  <button className="button-danger" onClick={() => statusMutation.mutate('cancelled')} style={{ marginTop: 8 }}>Cancel</button>
                )}
              </div>
            ),
          },
          {
            key: 'delivery', label: 'Proof of Delivery', content: (
              <div>
                <SectionHeader title="Proof of Delivery" />
                {!isDelivered ? (
                  <form className="inline-form" onSubmit={(e) => { e.preventDefault(); recordReceiptMutation.mutate(); }}>
                    <input placeholder="Received by (name)" value={podForm.receivedByName} onChange={(e) => setPodForm((s) => ({ ...s, receivedByName: e.target.value }))} required />
                    <input type="date" value={podForm.receivedDate} onChange={(e) => setPodForm((s) => ({ ...s, receivedDate: e.target.value }))} required />
                    <select value={podForm.condition} onChange={(e) => setPodForm((s) => ({ ...s, condition: e.target.value }))}>
                      {Object.entries(CONDITION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <button type="submit" disabled={recordReceiptMutation.isPending}>Confirm Delivery</button>
                  </form>
                ) : (
                  <dl className="detail-grid">
                    <dt>Received By</dt><dd>{waybill.received_by_name}</dd>
                    <dt>Received Date</dt><dd>{waybill.received_date ? formatDate(waybill.received_date) : '-'}</dd>
                    <dt>Condition</dt><dd>{CONDITION_LABELS[waybill.received_condition ?? ''] ?? '-'}</dd>
                  </dl>
                )}
              </div>
            ),
          },
          {
            key: 'documents', label: 'Documents', content: (
              <DocumentsSection
                documents={docsData?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('waybill', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('waybill', waybillId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('waybill', documentId)}
                queryKeyToInvalidate={['waybill-documents', waybillId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking', label: 'Tracking', content: (
              <div>
                <WorkflowTrackingTab entityType="waybill" entityId={waybillId} actionStageMap={WAYBILL_STAGES} statusLabels={WAYBILL_STATUS_LABELS} />
                <CommentsThread entityType="waybill" entityId={waybillId} section="logistics" heading="Comments for this Waybill" />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
